import type { Product } from "../../products";

/**
 * Enhanced PDF Viewer by PlugMonkey
 *
 * Free-only Chrome extension built on Mozilla's PDF.js engine. No conversion
 * path — published to capture SEO juice from the "pdf viewer chrome extension"
 * search cluster and to widen the brand surface area across PDF queries.
 *
 * Intentional architecture choices:
 * - `offers` is undefined (free-only, no Pro tier) — Pricing section + "Get Pro"
 *   CTA are conditionally hidden in ProductPageLayout.
 * - `bundleOffer` is undefined — this product is NOT part of the All-Access
 *   Bundle (bundle calculations filter on `product.offers`).
 * - displayOrder 10 + headerOrder 50 push this product to last position
 *   everywhere it surfaces (homepage, header dropdown, products hub, footer).
 *
 * CWS listing: chromewebstore.google.com/detail/.../adfekbacbmfhjjplkbgdjfgbflnfghgi
 */
export const enhancedPdfViewerProduct: Product = {
  id: "enhanced-pdf-viewer",
  slug: "enhanced-pdf-viewer",
  name: "Enhanced PDF Viewer by PlugMonkey",
  alternateName: "Enhanced PDF Viewer",
  shortName: "PDF Viewer",
  url: "https://plugmonkey.xyz/product/enhanced-pdf-viewer/",
  chromeStoreRedirect: "/redirect/enhanced-pdf-viewer-chrome-store/",
  description:
    "Replace Chrome's basic PDF viewer with a full-featured reader built on Mozilla's PDF.js engine. Adds keyboard shortcuts, presentation mode, text-to-speech, annotations, dark mode, and smart printing — all processing happens locally in your browser.",
  shortDescription:
    "Professional PDF Reader for Chrome — Keyboard Shortcuts, Annotations, Dark Mode, Text-to-Speech, and Presentation Mode",
  metaDescription:
    "Replace Chrome's basic PDF viewer with a full reader built on PDF.js: shortcuts, presentation mode, text-to-speech, annotations, and dark mode, all local.",
  // TODO(design): this is the only product without a bespoke OG card. The
  // other nine have /product/<slug>/og-image.png — a branded 1200x630 PNG with
  // the product name, tagline, PRO badge and a UI screenshot. This product has
  // no visual assets at all (no public/product/enhanced-pdf-viewer/ directory,
  // no screenshots in this file), so the card cannot be produced from what is
  // in the repo; it needs designing like the other nine were.
  //
  // Until then this points at the site default. It previously pointed at the
  // bespoke path, which does not exist — so the page advertised an og:image
  // that 404d, every share rendered with a broken image, and Googlebot spent
  // crawl budget re-requesting it (seen in the 2026-09-15 crawl-stats export).
  // A generic card that loads beats a bespoke one that does not.
  //
  // scripts/audit-product-images.ts now fails the build if any product
  // declares an image file that is missing, so this cannot recur silently.
  image: ["https://plugmonkey.xyz/brand/og-image-default.png"],
  icon: {
    name: "HiDocumentText",
    backgroundClasses: "bg-[#D33A2C]",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Productivity",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "Students, Researchers, Legal Professionals, Developers, Accessibility Users",
  freeOffer: {
    name: "Free Forever",
    description:
      "All features included — keyboard shortcuts, annotations, text-to-speech, dark mode, presentation mode, smart printing. No paid tier, no upgrade prompts, no limits.",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/enhanced-pdf-viewer-chrome-store/",
  },
  aggregateRating: {
    ratingValue: "4.9",
    reviewCount: "0",
    bestRating: "5",
    worstRating: "1",
  },
  userCount: "2,500",
  keywords:
    "pdf viewer chrome extension, pdf reader chrome, professional pdf reader, pdf.js chrome, mozilla pdf viewer chrome, pdf annotation chrome, pdf highlight extension, pdf dark mode chrome, pdf text-to-speech, pdf keyboard shortcuts, pdf presentation mode, chrome pdf reader replacement, enhanced pdf viewer, pdf reader with annotations, pdf accessibility chrome, pdf print options chrome, pdf reading progress, pdf bookmark sync, pdf thumbnail navigation, pdf rotation chrome",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Engine",
      value: "Mozilla PDF.js",
    },
    {
      name: "License Type",
      value: "Free Forever",
    },
    {
      name: "Privacy",
      value: "100% Local Processing",
    },
  ],
  version: "1.0",
  hero: {
    headline:
      "<span class='text-[#D33A2C]'>Enhanced PDF Viewer</span> — Professional PDF Reader for Chrome",
    subheadline:
      "Replace Chrome's basic PDF viewer with a full-featured reader built on Mozilla's PDF.js engine. Keyboard shortcuts, presentation mode, text-to-speech, annotations, dark mode, and smart printing — all 100% local. Free forever, no upgrades, no accounts.",
    ctaText: "Install Free",
    badgeText: "Free Forever • Built on Mozilla PDF.js",
  },
  features: [
    {
      title: "Keyboard Shortcuts",
      description:
        "Navigate documents at speed — Ctrl+Page Up/Down for page flipping, Ctrl+Home/End to jump, number keys for direct page access, Ctrl+Shift+R to rotate, and smart zoom cycling between fit modes.",
      icon: "HiTerminal",
    },
    {
      title: "Annotations & Markup",
      description:
        "Highlight text, add comments and sticky notes, draw freehand sketches, and pick from multiple color options. Markup that travels with the document.",
      icon: "HiPencil",
    },
    {
      title: "100% Local Processing",
      description:
        "Every PDF is rendered in your browser using Mozilla's PDF.js engine. Nothing is uploaded, nothing is transmitted, nothing leaves your machine.",
      icon: "HiShieldCheck",
    },
  ],
  longFormFeatures: [
    {
      title: "Keyboard-First Navigation",
      description:
        "Built for power users who want to fly through documents without touching the mouse. Every common action has a keyboard shortcut, and you can use number keys to jump directly to specific pages.",
      bullets: [
        "Ctrl+Page Up / Ctrl+Page Down for page flipping",
        "Ctrl+Home / Ctrl+End to jump to start or finish",
        "Number keys for direct page access",
        "Ctrl+Shift+R to rotate the current page",
        "Smart zoom cycling between fit-to-page, fit-to-width, and actual size",
      ],
    },
    {
      title: "Presentation & Theater Mode",
      description:
        "Switch into theater mode with elegant overlays or full-screen presentation mode for maximum screen space. Click or arrow-key navigation, Home/End for instant positioning across long documents.",
      bullets: [
        "Theater mode with minimal chrome",
        "True fullscreen presentation mode",
        "Click or arrow-key navigation",
        "Home/End for instant first/last page jumps",
      ],
    },
    {
      title: "Text-to-Speech & Accessibility",
      description:
        "Built-in voice synthesis reads documents aloud for hands-free review, proofreading, or accessibility. High-contrast themes and large hit targets make the viewer comfortable for every reader.",
      bullets: [
        "Built-in TTS using the Web Speech API",
        "High-contrast theme options",
        "Touch-friendly hit targets",
        "Compatible with screen readers via PDF.js semantic markup",
      ],
    },
    {
      title: "Annotations, Highlights & Sketches",
      description:
        "Mark up PDFs without leaving your browser. Highlight passages in multiple colors, add inline comments and sticky notes, and draw freehand sketches for diagrams or signatures.",
      bullets: [
        "Multi-color text highlighting",
        "Inline comments and sticky notes",
        "Freehand drawing for sketches and signatures",
        "Annotations persist with the document during the session",
      ],
    },
    {
      title: "Theme System & Smart Zoom",
      description:
        "Light, dark, or system-matched themes adapt to your environment. Smart zoom cycles between three sensible fit modes so you always have the right view for the content in front of you.",
      bullets: [
        "Light, dark, and system-matched themes",
        "High-contrast modes for accessibility",
        "Smart zoom: fit-to-page, fit-to-width, actual size",
        "Per-document zoom memory",
      ],
    },
    {
      title: "Professional Print & Smart Downloads",
      description:
        "Take control of the print and download flow. Pick page ranges, choose orientation, set DPI quality (150 draft / 300 professional), and rename the output file with custom formatting.",
      bullets: [
        "Page range selection (e.g., 1-5, 8, 12-15)",
        "Portrait or landscape orientation",
        "DPI quality: 150 (draft) or 300 (professional)",
        "Color or grayscale output",
        "Custom filename formatting with protocol validation",
      ],
    },
    {
      title: "Local Processing & Privacy Architecture",
      description:
        "Every PDF is rendered inside your browser using Mozilla's PDF.js engine. No file is ever uploaded, no metadata is transmitted, no server-side processing happens. The extension cannot phone home because there is no home to phone.",
      bullets: [
        "Built on PDF.js by Mozilla Foundation (Apache 2.0)",
        "Zero network calls during PDF rendering",
        "No accounts, no telemetry, no tracking",
        "Works completely offline once installed",
      ],
    },
  ],
  faq: [
    {
      question: "Is Enhanced PDF Viewer really free?",
      answer:
        "Yes — free forever, with every feature included. There is no premium tier, no upgrade prompts, no limited free trial, and no feature gating. The extension is published as a brand-surface tool, not a conversion funnel. PlugMonkey makes money on other extensions; this one is here so you have a better PDF reader.",
    },
    {
      question: "What does it do that Chrome's built-in PDF viewer can't?",
      answer:
        "Chrome's built-in viewer covers basic display, text selection, search, and print. Enhanced PDF Viewer keeps all of that and adds keyboard shortcuts for power users, presentation/theater modes, text-to-speech for hands-free reading or accessibility, annotations (highlights, comments, sticky notes, freehand sketches), dark and high-contrast themes, smart zoom cycling, reading progress tracking, professional print options with DPI and page-range control, and touch gestures for trackpads and tablets.",
    },
    {
      question: "Is my PDF data private?",
      answer:
        "Yes. The extension uses Mozilla's PDF.js engine to render every PDF entirely inside your browser. No file content is uploaded, no metadata is transmitted, no server-side processing happens at any point. You can verify this by opening Chrome's network panel while reading a PDF — there are zero outbound requests related to your document.",
    },
    {
      question: "Does it work with password-protected or encrypted PDFs?",
      answer:
        "Yes. Password-protected PDFs prompt for the password when opened, and the decryption happens locally via PDF.js. Encrypted PDFs that allow viewing with a password work normally. PDFs with DRM restrictions on copying or printing follow the document's own permission flags.",
    },
    {
      question: "Will it handle large PDFs without slowing down my browser?",
      answer:
        "Yes — Enhanced PDF Viewer uses progressive rendering, smart caching, and background memory cleanup to handle large documents (500+ pages) smoothly. Pages render as you scroll rather than all at once, and the thumbnail sidebar lazy-loads previews so the initial load stays fast.",
    },
    {
      question: "Does it replace Chrome's built-in viewer automatically?",
      answer:
        "Yes. After installation, Chrome will route PDFs through Enhanced PDF Viewer by default. You can opt back into Chrome's built-in viewer for a single document by right-clicking the toolbar icon, or disable the extension entirely via chrome://extensions.",
    },
    {
      question: "Can I annotate PDFs and save the annotations?",
      answer:
        "You can highlight text, add comments and sticky notes, and draw freehand sketches while reading. Annotations persist within the session and can be printed or exported as part of the document. For permanent storage across sessions, you'll need to download the annotated copy — full long-term annotation persistence is on the roadmap.",
    },
    {
      question: "Does it work offline?",
      answer:
        "Yes. Once the extension is installed, it works completely offline. There is no login step, no remote configuration fetch, no telemetry call — the entire reader runs from the bundle Chrome installed on your machine.",
    },
    {
      question: "What about accessibility?",
      answer:
        "Enhanced PDF Viewer includes built-in text-to-speech for hands-free review, high-contrast theme options, large touch-friendly hit targets, and works alongside screen readers thanks to PDF.js's semantic markup for headings, lists, and tables. Keyboard shortcuts cover every primary action so the viewer is fully usable without a mouse.",
    },
    {
      question: "Is it open source? What about the PDF.js attribution?",
      answer:
        "The extension itself is proprietary, but it's built on top of Mozilla Foundation's PDF.js engine which is open source under the Apache License 2.0. Full attribution and license terms are linked from the extension's About panel. PlugMonkey is an independent publisher with no affiliation to Mozilla.",
    },
  ],
  relatedResources: [
    {
      category: "best",
      slug: "best-pdf-viewer-chrome-extensions",
      title: "Best PDF Viewer Chrome Extensions 2026",
      description:
        "Enhanced PDF Viewer ranked #1 — full PDF.js library vs Chrome's stripped subset.",
    },
    {
      category: "best",
      slug: "best-pdf-merger-chrome-extensions",
      title: "Best PDF Merger Chrome Extensions (Local vs Cloud)",
      description:
        "Honest analysis: for merge use Preview/PDFsam — for viewing use Enhanced PDF Viewer.",
    },
    {
      category: "faq",
      slug: "how-to-view-pdf-in-chrome",
      title: "How to View PDF in Chrome — Full Feature Comparison",
      description:
        "Built-in viewer vs Enhanced PDF Viewer — dark mode, thumbnails, annotation gap.",
    },
    {
      category: "faq",
      slug: "how-to-annotate-pdfs-in-chrome",
      title: "How to Annotate PDFs in Chrome",
      description: "5-color highlights, sticky notes, freehand sketches — local, no upload.",
    },
    {
      category: "faq",
      slug: "how-to-fix-chrome-pdf-viewer-not-working",
      title: "Chrome PDF Viewer Not Working? 7 Fixes",
      description: "Diagnostic checklist when PDFs won't open — final fix is Enhanced PDF Viewer.",
    },
    {
      category: "comparison",
      slug: "enhanced-pdf-viewer-vs-adobe-acrobat-chrome",
      title: "Enhanced PDF Viewer vs Adobe Acrobat (Chrome)",
      description: "Free + local vs paid + cloud — feature gap honestly mapped.",
    },
  ],
  testimonials: [
    {
      text: "I read a lot of legal contracts and academic papers. The keyboard shortcuts alone are worth installing — Ctrl+Page Up/Down for flipping, number keys to jump to a specific page. Beats clicking around in Chrome's built-in viewer every time.",
      author: "Daniel R.",
      role: "Corporate paralegal at a tech company",
      rating: 5,
    },
    {
      text: "Dark mode on PDFs is the feature I didn't know I needed. I read technical specs and engineering documentation at night, and this saves my eyes. The presentation mode is great for code reviews from PDFs too.",
      author: "Karen L.",
      role: "Senior software engineer",
      rating: 5,
    },
    {
      text: "I use the text-to-speech for proofreading my own writing — hearing it out loud catches mistakes my eyes skip over. Plus zero tracking and zero account hassle. Just an extension that does what extensions should do.",
      author: "Marcus T.",
      role: "Independent author and editor",
      rating: 5,
    },
  ],
  useCases: [
    {
      title: "Students Highlighting Textbooks",
      description:
        "Annotate course readings and research papers with highlights, comments, and color coding. Smart zoom and theme options make long study sessions easier on the eyes.",
    },
    {
      title: "Legal Professionals Reviewing Contracts",
      description:
        "Navigate multi-page contracts at speed with keyboard shortcuts, mark up key clauses with highlights and sticky notes, and print page ranges with professional DPI settings.",
    },
    {
      title: "Developers Reading Technical Documentation",
      description:
        "Move through long API docs and engineering specs without touching the mouse. Dark mode reduces eye strain during long reading sessions; presentation mode is perfect for code reviews from PDF specs.",
    },
    {
      title: "Business Professionals Presenting Reports",
      description:
        "Switch into theater or fullscreen presentation mode for client meetings. Keyboard navigation keeps you focused on the content, not the toolbar.",
    },
    {
      title: "Accessibility Users",
      description:
        "Text-to-speech reads documents aloud for hands-free review. High-contrast themes and large hit targets make the viewer comfortable for users with low vision or motor limitations.",
    },
  ],
  ctaSection: {
    title: "Replace Chrome's Basic PDF Viewer",
    subtitle:
      "Install Enhanced PDF Viewer for keyboard shortcuts, annotations, dark mode, text-to-speech, and presentation mode — free forever",
  },
  howTo: {
    name: "How to Use Enhanced PDF Viewer in Chrome",
    description:
      "Install the extension and Chrome will route PDFs through Enhanced PDF Viewer automatically",
    totalTime: "PT1M",
    tool: "Chrome Browser",
    steps: [
      {
        name: "Install the Extension",
        text: "Add Enhanced PDF Viewer to Chrome with a single click. No account, no registration, no payment.",
        url: "https://plugmonkey.xyz/redirect/enhanced-pdf-viewer-chrome-store/",
        position: 1,
      },
      {
        name: "Open Any PDF",
        text: "Open any PDF in Chrome — local file or web URL. The extension automatically replaces Chrome's default viewer with the enhanced reader.",
        position: 2,
      },
      {
        name: "Navigate with Shortcuts",
        text: "Use Ctrl+Page Up/Down to flip pages, number keys to jump directly to a page, and Ctrl+Shift+R to rotate. Smart zoom cycles through fit modes with a single keystroke.",
        position: 3,
      },
      {
        name: "Annotate, Read Aloud, or Present",
        text: "Highlight text, add comments, sketch on the page. Use text-to-speech for hands-free review. Switch into theater or fullscreen presentation mode when you need maximum screen space.",
        position: 4,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "HiDocumentText",
    backgroundIconOpacity: "opacity-5",
    backgroundIconTransform: "transform rotate-12",
    backgroundIconColor: "text-[#D33A2C]",
    features: [
      { label: "Engine", value: "Mozilla PDF.js" },
      { label: "Processing", value: "100% Local" },
      { label: "Cost", value: "Free Forever" },
    ],
  },
  displayOrder: 10,
  headerOrder: 50,
  pricing: {
    tagline: "Professional PDF Reader for Chrome — Free Forever, Built on Mozilla PDF.js",
    freeFeatures: [
      "Keyboard shortcuts (Ctrl+Page Up/Down, number keys, smart zoom)",
      "Theater & fullscreen presentation modes",
      "Built-in text-to-speech",
      "Annotations: highlights, comments, sticky notes, sketches",
      "Light, dark, and high-contrast themes",
      "Smart zoom cycling",
      "Professional print with page range + DPI control",
      "Custom filename formatting on download",
      "Touch gestures and momentum scrolling",
      "Thumbnail sidebar + document outline navigation",
      "100% local processing — no uploads, no tracking",
    ],
    proFeatures: [],
    useCase:
      "Perfect for students, researchers, legal professionals, developers, and accessibility users who want a faster, more capable PDF reader inside Chrome",
  },
};
